import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { MoreVertical, Trash2 } from "lucide-react"
import * as React from "react"

interface TagActionMenuProps {
  label: string
  onRename?: (value: string) => Promise<void> | void
  onDelete?: () => Promise<void> | void
}

export function TagActionMenu({
  label,
  onRename,
  onDelete,
}: TagActionMenuProps) {
  const { translate } = useTranslation()
  const [open, setOpen] = React.useState(false)
  const [draftLabel, setDraftLabel] = React.useState("")
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen)
      if (nextOpen) {
        setDraftLabel(label)
      } else {
        setDraftLabel("")
      }
    },
    [label],
  )

  const handleRename = React.useCallback(async () => {
    const trimmed = draftLabel.trim()
    if (!onRename || !trimmed) {
      setDraftLabel(label)
      return
    }
    if (trimmed === label) {
      setOpen(false)
      setDraftLabel("")
      return
    }
    try {
      setIsSubmitting(true)
      await onRename(trimmed)
      setOpen(false)
      setDraftLabel("")
    } catch {
      // onRename already told the user why; stay open so the name can be fixed.
    } finally {
      setIsSubmitting(false)
    }
  }, [draftLabel, label, onRename])

  const handleDelete = React.useCallback(async () => {
    if (!onDelete) return
    try {
      setIsSubmitting(true)
      await onDelete()
      setOpen(false)
    } finally {
      setIsSubmitting(false)
    }
  }, [onDelete])

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
          aria-label={translate("KNOWLEDGEBASE/TAG_ACTIONS")}
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <MoreVertical className="size-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={8}
        className="w-56 p-2"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="space-y-2">
          {onRename && (
            <Input
              value={draftLabel}
              onChange={(event) => setDraftLabel(event.target.value)}
              onBlur={() => void handleRename()}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault()
                  void handleRename()
                }
              }}
              disabled={isSubmitting}
              aria-label={translate("KNOWLEDGEBASE/TAG_RENAME_LABEL")}
              placeholder={translate("KNOWLEDGEBASE/TAG_RENAME_LABEL")}
              className="h-8"
            />
          )}
          {onDelete && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => void handleDelete()}
              disabled={isSubmitting}
              className="w-full justify-start text-destructive hover:text-destructive"
            >
              <Trash2 className="size-4" />
              {translate("KNOWLEDGEBASE/TAG_DELETE_ACTION")}
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
